import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { api } from "@/lib/apiClient";
import { pitchMatchesBookingCategory } from "@/lib/pitchAssignment";
import { ensurePitchSuggestion } from "@/lib/pitchAssignmentOptions";
import { SitePlanCanvas } from "@/components/site-plan/SitePlanCanvas";
import type { PitchHighlightRole } from "@/lib/sitePlan";
import {
  type SitePlanPitchStatus,
  type SitePlanSettings,
  type SitePlanStatusResponse,
} from "@/lib/sitePlan";
import type { Booking, Pitch } from "@/types/camping";

type AvailabilityResponse = {
  available: { pitch_id: string; code: string }[];
};

export type CheckInPitchAssignmentMode = "reception" | "tablet";

export type CheckInPitchChoice = {
  id: string;
  code: string;
  label: string | null;
};

interface CheckInPitchSelectionStepProps {
  booking: Booking;
  selectedPitchIds: string[];
  onSelectedPitchIdsChange: (ids: string[]) => void;
  assignmentMode: CheckInPitchAssignmentMode;
  onAssignmentModeChange: (mode: CheckInPitchAssignmentMode) => void;
  onPitchChoicesChange?: (choices: CheckInPitchChoice[]) => void;
}

export function CheckInPitchSelectionStep({
  booking,
  selectedPitchIds,
  onSelectedPitchIdsChange,
  assignmentMode,
  onAssignmentModeChange,
  onPitchChoicesChange,
}: CheckInPitchSelectionStepProps) {
  const [planImage, setPlanImage] = useState<string | null>(null);
  const [status, setStatus] = useState<SitePlanStatusResponse | null>(null);
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [availableIds, setAvailableIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"plan" | "list">("plan");

  const availabilityParams = useMemo(() => {
    const params = new URLSearchParams({
      arrival: booking.arrival_date,
      departure: booking.departure_date,
      exclude_booking_id: booking.id,
    });
    if (booking.vehicle_length_m) {
      params.set("vehicle_length_m", booking.vehicle_length_m);
    }
    return params.toString();
  }, [booking.arrival_date, booking.departure_date, booking.id, booking.vehicle_length_m]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await ensurePitchSuggestion(booking.id, { force: true });
      const [settings, planStatus, availability, pitchList] = await Promise.all([
        api.get<SitePlanSettings>("/api/v1/settings/site-plan?include_image=true"),
        api.get<SitePlanStatusResponse>(
          `/api/v1/site-plan/status?reference_date=${encodeURIComponent(booking.arrival_date)}`,
        ),
        api.get<AvailabilityResponse>(`/api/v1/availability?${availabilityParams}`),
        api.get<Pitch[]>("/api/v1/pitches"),
      ]);
      setPlanImage(settings.image_data);
      setStatus(planStatus);
      setPitches(pitchList);
      setAvailableIds(new Set(availability.available.map((p) => p.pitch_id)));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Platzplan konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [booking.arrival_date, booking.id, availabilityParams]);

  useEffect(() => {
    void load();
  }, [load]);

  const categoryPitchIds = useMemo(() => {
    return new Set(
      pitches
        .filter((p) => pitchMatchesBookingCategory(booking.pitch_category_id, p.category_id))
        .map((p) => p.id),
    );
  }, [pitches, booking.pitch_category_id]);

  const visiblePitchIds = useMemo(() => {
    if (!status) return new Set<string>();
    return new Set(
      status.pitches
        .filter(
          (p) =>
            categoryPitchIds.has(p.pitch_id) &&
            p.map_polygon &&
            p.map_polygon.length >= 3,
        )
        .map((p) => p.pitch_id),
    );
  }, [status, categoryPitchIds]);

  const selectablePitchIds = useMemo(() => {
    if (!status) return [];
    return status.pitches
      .filter(
        (p) =>
          visiblePitchIds.has(p.pitch_id) &&
          availableIds.has(p.pitch_id),
      )
      .map((p) => p.pitch_id);
  }, [status, visiblePitchIds, availableIds]);

  const allSelectableChosen =
    selectablePitchIds.length > 0 &&
    selectablePitchIds.every((id) => selectedPitchIds.includes(id));

  const pitchHighlights = useMemo(() => {
    const map: Record<string, PitchHighlightRole> = {};
    if (!status) return map;
    for (const pitch of status.pitches) {
      if (!visiblePitchIds.has(pitch.pitch_id)) continue;
      if (selectedPitchIds.includes(pitch.pitch_id)) {
        map[pitch.pitch_id] = "assigned";
      } else if (availableIds.has(pitch.pitch_id)) {
        map[pitch.pitch_id] = "none";
      } else {
        map[pitch.pitch_id] = "dimmed";
      }
    }
    return map;
  }, [status, selectedPitchIds, availableIds, visiblePitchIds]);

  const overlays = useMemo(() => {
    if (!status) return [];
    return status.pitches
      .filter((p): p is SitePlanPitchStatus & { map_polygon: NonNullable<SitePlanPitchStatus["map_polygon"]> } =>
        Boolean(visiblePitchIds.has(p.pitch_id) && p.map_polygon && p.map_polygon.length >= 3),
      )
      .map((p) => ({
        id: p.pitch_id,
        code: p.code,
        map_polygon: p.map_polygon,
        status_kind: p.status_kind,
        transition_hint: p.transition_hint,
        booking_status: p.booking_status,
      }));
  }, [status, visiblePitchIds]);

  const listPitches = useMemo(() => {
    if (!status) return [];
    return status.pitches
      .filter((p) => selectablePitchIds.includes(p.pitch_id))
      .sort((a, b) => a.code.localeCompare(b.code, "de"));
  }, [status, selectablePitchIds]);

  useEffect(() => {
    onPitchChoicesChange?.(
      listPitches.map((pitch) => ({
        id: pitch.pitch_id,
        code: pitch.code,
        label: pitch.label,
      })),
    );
  }, [listPitches, onPitchChoicesChange]);

  function handlePitchSelect(pitchId: string) {
    if (!selectablePitchIds.includes(pitchId)) return;
    if (assignmentMode === "reception") {
      onSelectedPitchIdsChange(
        selectedPitchIds.length === 1 && selectedPitchIds[0] === pitchId ? [] : [pitchId],
      );
      return;
    }
    onSelectedPitchIdsChange(
      selectedPitchIds.includes(pitchId)
        ? selectedPitchIds.filter((id) => id !== pitchId)
        : [...selectedPitchIds, pitchId],
    );
  }

  function handleSelectAll() {
    onSelectedPitchIdsChange([...selectablePitchIds]);
  }

  if (loading) {
    return <p className="text-sm text-label-secondary">Verfügbare Plätze werden geladen…</p>;
  }

  if (error) {
    return <p className="text-sm text-status-red">{error}</p>;
  }

  return (
    <div className="space-y-3">
      <div
        className="inline-flex rounded-lg border border-separator p-0.5 text-xs"
        role="group"
        aria-label="Wer den Stellplatz festlegt"
      >
        <button
          type="button"
          className={clsx(
            "rounded-md px-2.5 py-1 font-medium",
            assignmentMode === "reception" ? "bg-accent text-label-on-accent" : "text-label-secondary",
          )}
          aria-pressed={assignmentMode === "reception"}
          onClick={() => onAssignmentModeChange("reception")}
        >
          Rezeption
        </button>
        <button
          type="button"
          className={clsx(
            "rounded-md px-2.5 py-1 font-medium",
            assignmentMode === "tablet" ? "bg-accent text-label-on-accent" : "text-label-secondary",
          )}
          aria-pressed={assignmentMode === "tablet"}
          onClick={() => onAssignmentModeChange("tablet")}
        >
          Gasttablet
        </button>
      </div>
      <p className="text-sm text-label-secondary">
        {assignmentMode === "reception"
          ? "Wählen Sie genau einen freien Platz. Der Check-in läuft vollständig an der Rezeption – der Gast muss am Tablet nichts antippen."
          : "Wählen Sie einen oder mehrere freie Plätze der gebuchten Kategorie. Bei mehreren Optionen wählt der Gast am Tablet; bei nur einem Platz geht es direkt zur Übersicht."}
      </p>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-label-tertiary">
          {selectablePitchIds.length} Plätze verfügbar
          {selectedPitchIds.length > 0 ? ` · ${selectedPitchIds.length} ausgewählt` : ""}
          {assignmentMode === "reception" && selectedPitchIds.length > 1
            ? " · für die Rezeption nur einen Platz antippen"
            : ""}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {assignmentMode === "tablet" && selectablePitchIds.length > 1 ? (
            <button
              type="button"
              className="rounded-lg border border-separator px-2.5 py-1 text-xs font-medium text-label-secondary hover:bg-fill-tertiary disabled:opacity-50"
              onClick={handleSelectAll}
              disabled={allSelectableChosen}
            >
              Alle auswählen
            </button>
          ) : null}
          <div className="inline-flex rounded-lg border border-separator p-0.5 text-xs">
          <button
            type="button"
            className={clsx(
              "rounded-md px-2.5 py-1 font-medium",
              viewMode === "plan" ? "bg-accent text-label-on-accent" : "text-label-secondary",
            )}
            onClick={() => setViewMode("plan")}
          >
            Plan
          </button>
          <button
            type="button"
            className={clsx(
              "rounded-md px-2.5 py-1 font-medium",
              viewMode === "list" ? "bg-accent text-label-on-accent" : "text-label-secondary",
            )}
            onClick={() => setViewMode("list")}
          >
            Liste
          </button>
          </div>
        </div>
      </div>
      {viewMode === "plan" ? (
        <SitePlanCanvas
          imageData={planImage}
          pitches={overlays}
          badgeScale={status ? Number(status.badge_scale) || 1 : 1}
          mode="view"
          selectedPitchIds={selectedPitchIds}
          pitchHighlights={pitchHighlights}
          selectablePitchIds={selectablePitchIds}
          onPitchSelect={handlePitchSelect}
        />
      ) : (
        <ul className="max-h-64 space-y-1 overflow-y-auto rounded-xl border border-separator p-2">
          {listPitches.map((pitch) => {
            const selected = selectedPitchIds.includes(pitch.pitch_id);
            return (
              <li key={pitch.pitch_id}>
                <button
                  type="button"
                  className={clsx(
                    "flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm",
                    selected
                      ? "bg-accent text-label-on-accent"
                      : "hover:bg-fill-tertiary text-label",
                  )}
                  onClick={() => handlePitchSelect(pitch.pitch_id)}
                >
                  <span className="font-medium">{pitch.code}</span>
                  {pitch.label ? (
                    <span className={clsx("text-xs", selected ? "opacity-90" : "text-label-secondary")}>
                      {pitch.label}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
